import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, SubscriptionPlanStatus, PlanState } from 'src/app/core/graphql/generated/gen-types';
import { UserService, SubscriptionStateService } from 'src/app/core/services';
import { ResponsiveService, ScreenSize } from 'src/app/core/services/admin/responsive.service';
import { SmsService } from 'src/app/core/services/sms/sms.service';
import { TenantSettingService } from 'src/app/core/services/tenantSetting/tenant-setting.service';
import { SnackBar } from 'src/app/core/utility/snackBar';
import { DialogReleasenotesComponent } from 'src/app/features/dialog/dialog-releasenotes/dialog-releasenotes.component';
import { environment } from 'src/environments/environment';

@Component({
	templateUrl: './main-layout.component.html',
	styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit {
	@ViewChild('drawer') drawer: MatDrawer;

	sideNavOpened = true;
	drawerMode = 'over';
	mode: string = 'side';
	mobileRoute: boolean = false;
	xsmall: boolean = false;
	subscription: Subscription;
	releaseNotesCount: number;
	unreadSms: number;
	unreadSmsSubscription;
	isOperator = false;
	guards = environment.componentGuards;
	isMobile = false;
	planState: PlanState;

	constructor(
		private responsiveService: ResponsiveService,
		private router: Router,
		private snackBar: SnackBar,
		private tenantSettingService: TenantSettingService,
		private userService: UserService,
		private dialog: MatDialog,
		private smsService: SmsService,
		private subscriptionStateService: SubscriptionStateService,
	) {
		this.responsiveService.viewMobileChanged.subscribe((isMobile) => {
			this.isMobile = isMobile;
		});
		this.userService.userProfileChanged.subscribe((userProfile) => {
			this.isOperator = this.guards.dashboard.sms.includes(userProfile?.role);
		});
		this.tenantSettingService.getTenantSetting().valueChanges.subscribe((result) => {
			this.subscription = result.data.tenantSetting.subscription;
			this.planState = (this.subscription?.planState as any) || null;
			if (this.subscription?.planState) {
				this.subscriptionStateService.updateState(this.subscription.planState as PlanState);
			}
			if (this.subscription?.isBlocked !== undefined) {
				this.subscriptionStateService.updateBlockedState(this.subscription.isBlocked);
			}
		});
		this.tenantSettingService.getReleaseNotesCount().valueChanges.subscribe((result) => {
			this.releaseNotesCount = result.data.releaseNotesCount;
		});
	}

	isDrawerVisible(): boolean {
		if (!this.drawer) return false;
		return this.drawer.opened;
	}

	ngOnDestroy() {
		if (this.unreadSmsSubscription) {
			this.unreadSmsSubscription.unsubscribe();
		}
	}

	ngOnInit() {
		// get sms count
		this.unreadSmsSubscription = this.smsService.getUnreadSmsCount().subscribe({
			next: (count) => {
				this.unreadSms = count;
			},
		});
		this.smsService.getSmsDrawerStatus().subscribe((status) => {
			if (this.drawer) {
				if (status) {
					this.drawer.open();
				} else {
					this.drawer.close();
				}
			}
		});
		// observe the viewport, responsive in case of Mobile
		this.responsiveService.XSmallChanged.subscribe(
			(xsmall) => {
				this.xsmall = xsmall;
				if (xsmall || this.mobileRoute) {
					this.mode = 'push';
					this.sideNavOpened = false;
				} else {
					this.mode = 'side';
					this.sideNavOpened = true;
				}
			},
			(err) => this.snackBar.openSnackBarError(err.message),
		);

		// observe the route and close sidenav when mobile route is taken
		if (this.router.url.includes('mobile')) {
			this.mobileRoute = true;
			this.mode = 'push';
			this.sideNavOpened = false;
		}

		this.router.events.subscribe(
			(event) => {
				if (event instanceof NavigationEnd) {
					if (event.url.includes('mobile')) {
						this.mobileRoute = true;
						this.mode = 'push';
						this.sideNavOpened = false;
					} else {
						this.mobileRoute = false;
						this.mode = this.xsmall ? 'push' : 'side';
						this.sideNavOpened = !this.xsmall;
					}
				}
			},
			(err) => this.snackBar.openSnackBarError(err.message),
		);
	}

	toggleMenu() {
		this.sideNavOpened = !this.sideNavOpened;
	}

	showReleaseNotes() {
		this.dialog.open(DialogReleasenotesComponent, {
			width: window.innerWidth <= 360 ? '95%' : '70%',
			maxWidth: 'none',
		});
	}

	toggleDrawerMode() {
		this.drawerMode = this.drawerMode === 'side' ? 'over' : 'side';
	}

	toggleDrawerStatus() {
		this.smsService.setSmsDrawerStatus(!this.drawer.opened);
	}

	warningMessage() {
		const toolbarHeight = 64;
		const messageHeight = 50;
		let margin = toolbarHeight;

		const releaseNotesWarning = !!this.releaseNotesCount;
		const smsSubscriptionWarning = this.subscription?.sms?.pctUsed >= 95 && !this.subscription?.sms?.ignoreWarning;
		const { planExpiringWarning, planExpiredWarning, planInvalidWarning } = this.computePlanWarnings();

		margin += planInvalidWarning ? messageHeight : 0;
		margin += planExpiringWarning ? messageHeight : 0;
		margin += planExpiredWarning ? messageHeight : 0;
		margin += smsSubscriptionWarning ? messageHeight : 0;
		margin += releaseNotesWarning ? messageHeight : 0;

		return {
			releaseNotesWarning,
			smsSubscriptionWarning,
			planExpiringWarning,
			planExpiredWarning,
			planInvalidWarning,
			margin: `${margin}px`,
		};
	}

	/**
	 * Calculate days remaining until plan expiration or grace period end.
	 * Used in EXPIRING and EXPIRED_GRACE states.
	 */
	getDaysRemaining(): number {
		const plan = this.subscription?.plan;
		const gracePeriodDays = this.subscription?.gracePeriodDays ?? 0;

		if (!plan?.validUntil) {
			return 0;
		}

		const now = new Date();
		const validUntil = new Date(plan.validUntil);
		const msPerDay = 24 * 60 * 60 * 1000;

		const graceEnd = new Date(validUntil.getTime() + gracePeriodDays * msPerDay);
		if (now >= validUntil && now < graceEnd) {
			const daysRemaining = Math.ceil((graceEnd.getTime() - now.getTime()) / msPerDay);
			return Math.max(0, daysRemaining);
		}

		const daysRemaining = Math.ceil((validUntil.getTime() - now.getTime()) / msPerDay);
		return Math.max(0, daysRemaining);
	}

	/**
	 * Calculate days remaining in grace period for invalid plan.
	 * Used in INVALID state with invalidSince timestamp.
	 */
	getInvalidGraceDaysRemaining(): number {
		const invalidSince = this.subscription?.plan?.invalidSince;
		const gracePeriodDays = this.subscription?.gracePeriodDays ?? 0;

		if (!invalidSince) {
			return 0;
		}

		const now = new Date();
		const invalidDate = new Date(invalidSince);
		const msPerDay = 24 * 60 * 60 * 1000;

		const graceEnd = new Date(invalidDate.getTime() + gracePeriodDays * msPerDay);
		const daysRemaining = Math.ceil((graceEnd.getTime() - now.getTime()) / msPerDay);
		return Math.max(0, daysRemaining);
	}

	/**
	 * Check if invalid plan grace period has ended.
	 */
	isInvalidGracePeriodEnded(): boolean {
		const invalidSince = this.subscription?.plan?.invalidSince;
		const gracePeriodDays = this.subscription?.gracePeriodDays ?? 0;

		if (!invalidSince) {
			return false;
		}

		const now = new Date();
		const invalidDate = new Date(invalidSince);
		const msPerDay = 24 * 60 * 60 * 1000;
		const graceEnd = new Date(invalidDate.getTime() + gracePeriodDays * msPerDay);

		return now >= graceEnd;
	}

	private computePlanWarnings(): { planExpiringWarning: boolean; planExpiredWarning: boolean; planInvalidWarning: boolean } {
		const isInvalidPlan = !this.subscription?.plan?.valid;

		return {
			planExpiringWarning: !isInvalidPlan && (this.planState === PlanState.Expiring || this.planState === PlanState.ExpiredGrace),
			planExpiredWarning: !isInvalidPlan && this.planState === PlanState.ExpiredFinal,
			planInvalidWarning: isInvalidPlan && this.planState === PlanState.Active,
		};
	}
}
